export const prettifyNumber = (number: number, lang: 'fr' | 'de') => {
  return Intl.NumberFormat(lang + '-CH').format(number)
}

export const getExtendedName = (
  station: string,
  name: string,
  lang: 'fr' | 'de'
) => {
  const stationLocations: Record<string, string> =
    lang === 'fr'
      ? {
          PUY: 'Pully',
          CGI: 'Changins',
          GVE: 'Cointrin',
        }
      : {
          SMA: 'Fluntern',
          BAS: 'Binningen',
          BER: 'Zollikofen',
          GVE: 'Cointrin',
        }
  if (Object.keys(stationLocations).indexOf(station) >= 0) {
    return name === 'Lausanne'
      ? stationLocations[station]
      : name + '-' + stationLocations[station]
  } else {
    return name
  }
}

export const getHour = (timestamp: string, lang = 'fr') => {
  try {
    const date = new Date(timestamp)
    const params: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
    }
    return date
      .toLocaleTimeString(lang === 'fr' ? 'fr-FR' : 'de-DE', params)
      .replace(':', 'h')
  } catch (e) {
    console.warn(e)
    return timestamp
  }
}

export const getDate = (timestamp: string, lang = 'fr') => {
  try {
    const date = new Date(timestamp)
    const params: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
    }
    return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'de-DE', params)
  } catch (e) {
    console.warn(e)
    return timestamp
  }
}

export const getMonth = (number: number, lowercase = false, lang: string) => {
  const months =
    lang === 'fr'
      ? [
          null,
          'Janvier',
          'Février',
          'Mars',
          'Avril',
          'Mai',
          'Juin',
          'Juillet',
          'Août',
          'Septembre',
          'Octobre',
          'Novembre',
          'Décembre',
        ]
      : [
          null,
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ]
  if (lowercase) {
    return months[number]?.toLowerCase()
  } else {
    return months[number]
  }
}

export const getSentence = (
  value: number,
  category: string,
  lang: string,
  mean?: number | null
) => {
  let sentence = ''
  switch (category) {
    case 'percentageGroup':
      if (value <= 10) {
        return lang === 'fr' ? 'Beaucoup plus' : 'Viel mehr'
      } else {
        return lang === 'fr' ? 'Plus' : 'Mehr'
      }
    case 'temperature':
      const ratio = value / 100
      if (ratio > 0.9) {
        return lang === 'fr'
          ? 'Très souvent plus chaud que d’ordinaire'
          : 'Sehr oft wärmer als üblich'
      } else if (ratio > 0.5) {
        return lang === 'fr'
          ? 'Souvent plus chaud que d’ordinaire'
          : 'Häufig wärmer als üblich'
      } else {
        return lang === 'fr'
          ? 'Pas spécialement plus chaud'
          : 'Nicht besonders wärmer'
      }
    case 'rain':
      if (value < 0.75) {
        sentence =
          lang === 'fr' ? 'Plus sec que d’ordinaire' : 'Trockener als üblich'
      } else if (value > 1.25) {
        sentence =
          lang === 'fr' ? 'Plus humide que d’ordinaire' : 'Nasser als üblich'
      } else {
        sentence = lang === 'fr' ? 'Rien d’anormal' : 'Nicht ungewöhnlich'
      }
      break
    case 'tropicalDays':
      if (value > mean!) {
        sentence =
          lang === 'fr'
            ? 'Plus que la moyenne sur 30 ans'
            : `Mehr als normal (⌀ ${mean!} Tage)`
      } else if (value < mean!) {
        sentence =
          lang === 'fr'
            ? 'Moins que la moyenne sur 30 ans'
            : `Weniger als normal (⌀ ${mean!} Tage)`
      } else {
        sentence =
          lang === 'fr'
            ? 'Autant que la moyenne sur 30 ans'
            : `Entspricht der Norm (⌀ ${mean!} Tage)`
      }
      break
    case 'tropicalNights':
      if (value > 1 + mean!) {
        sentence =
          lang === 'fr'
            ? 'Plus que la moyenne sur 30 ans'
            : `Mehr als normal (⌀ ${mean!} Tage)`
      } else if (value < mean! - 1) {
        sentence =
          lang === 'fr'
            ? 'Moins que la moyenne sur 30 ans'
            : `Weniger als normal (⌀ ${mean!} Tage)`
      } else {
        sentence =
          lang === 'fr'
            ? 'Autant que la moyenne sur 30 ans'
            : `Entspricht der Norm (⌀ ${mean!} Tage)`
      }
      break
  }
  return sentence
}

export const getOrdinal = (rank: number, lang = 'fr') => {
  switch (rank) {
    case 1:
      return 'premier'
    case 2:
      return 'deuxième'
    case 3:
      return 'troisième'
    case 4:
      return 'quatrième'
  }
}

export const dayReached = (count: number) => {
  if (count === 0) {
    return 'aucun jour n’a'
  } else if (count === 1) {
    return 'un seul jour a'
  } else {
    return `${count} jours ont`
  }
}

export const evaluateExtremeRatio = (
  percentageFacts: PercentageGroup,
  lang: 'fr' | 'de'
) => {
  if (lang === 'fr') {
    let hot_cold = ''
    try {
      hot_cold = percentageFacts!.hot_cold.toString()
    } catch (e) {
      console.warn(e)
      return ''
    }
    return hot_cold === 'hottest'
      ? `Le pic de ${prettifyNumber(
          +percentageFacts!.latest_max,
          'fr'
        )} °C enregistré ${
          (percentageFacts!.day_relative as unknown as string) === 'today'
            ? 'aujourd’hui'
            : 'hier'
        } à ${getHour(
          percentageFacts!.latest_max_date as unknown as string,
          'fr'
        )} ` +
          (+percentageFacts!.step < 3
            ? `est le ${
                +percentageFacts!.position !== 1
                  ? getOrdinal(+percentageFacts!.position)
                  : ''
              } plus chaud`
            : `compte parmi les ${percentageFacts!.step}% les plus élevés`) +
          ` pour un ${getDate(
            percentageFacts!.latest_max_date as unknown as string,
            'fr'
          )} depuis 1980.
                      `
      : '' // We also have the data to create the same kind of text for the coldest / coldest 1%
  } else {
    // German text
    return ''
  }
}

export const evaluateRatio = (value: number) => {
  const threshold_big = 0.5
  const threshold_small = 0.25
  if (value < 1 - threshold_big) {
    return -2
  } else if (value < 1 - threshold_small) {
    return -1
  } else if (value <= 1 + threshold_small) {
    return 0
  } else if (value > 1 + threshold_small) {
    return 1
  } else {
    return 2
  }
}

export const getEvaluationRain = (value: number, lang: string) => {
  if (value < 0.5) {
    return lang === 'fr' ? 'beaucoup moins' : 'viel weniger'
  } else if (value < 0.75) {
    return lang === 'fr' ? 'moins' : 'weniger'
  } else if (value <= 1.25) {
    return lang === 'fr'
      ? 'plus ou moins autant'
      : 'zeitweise etwas mehr als auch etwas weniger'
  } else if (value > 1.5) {
    return lang === 'fr' ? 'beaucoup plus' : 'viel mehr'
  } else if (value > 1.25) {
    return lang === 'fr' ? 'davantage' : 'mehr'
  } else {
    return lang === 'fr' ? 'un peu plus' : 'etwas mehr'
  }
}
